import { useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import { useHoldings } from '../holdings/useHoldings';
import { useQuotes } from '../prices/usePrices';
import { useSnapshots } from '../dashboard/useSnapshots';
import { EM_DASH } from '../../shared/format';
import { Card, SectionLabel } from '../../shared/pixel';

/**
 * แสดงเวลาตามโซนกรุงเทพเสมอ ไม่อิงนาฬิกาเครื่อง
 *
 * ค่าที่เก็บใน Firestore เป็น UTC ทั้งหมด และ snapshot ตั้งชื่อ document ด้วยวันที่ไทย
 * ถ้าหน้านี้แสดงตามโซนของเครื่อง เวลาที่เห็นจะไม่ตรงกับวันที่ของ snapshot ตอนอยู่ต่างประเทศ
 */
function formatBangkok(date: Date): string {
  return date.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * ล้าง service worker กับ cache แล้วโหลดใหม่
 *
 * แอปเป็น PWA ที่ precache ตัวเองไว้ พอ deploy เวอร์ชันใหม่ เครื่องที่ติดตั้งไว้แล้ว
 * อาจยังเปิดของเก่าอยู่จนกว่าจะปิดแอปเปิดใหม่ ปุ่มนี้ทำให้ไม่ต้องเดาว่าต้องทำยังไง
 */
async function reloadLatestBuild(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in globalThis) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
  globalThis.location.reload();
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-line-soft py-2.5 last:border-0">
      <dt className="text-sm text-ink-400">{label}</dt>
      <dd className="font-mono text-[13px] text-ink-100">{children}</dd>
    </div>
  );
}

export function SettingsPage() {
  const { user, signOut } = useAuth();

  const holdingsQuery = useHoldings();
  const holdings = useMemo(() => holdingsQuery.data ?? [], [holdingsQuery.data]);
  const priceKeys = useMemo(() => holdings.map((holding) => holding.priceKey), [holdings]);

  const quotesQuery = useQuotes(priceKeys);
  /*
    ขอ snapshot ทั้งหมด (0) เพราะจำนวนวันที่มีประวัติคือตัวเลขที่คนมาดูหน้านี้อยากรู้ —
    มันตอบคำถามว่า "ทำไมกราฟยังไม่ขึ้น" ได้ตรง ๆ

    แลกกับการอ่านทั้ง collection ซึ่งโตวันละหนึ่ง document ปีหนึ่งราว 365 ชิ้นเล็ก ๆ
    ยอมรับได้สำหรับพอร์ตส่วนตัวที่เปิดหน้านี้นาน ๆ ครั้ง ถ้าวันหนึ่งมันใหญ่จนสะดุด
    ให้เปลี่ยนไปใช้ getCountFromServer แทนการดึงมานับเอง
  */
  const snapshotsQuery = useSnapshots(0);

  const [isReloading, setIsReloading] = useState(false);

  /*
    ใช้ราคาที่ **เก่าที่สุด** ไม่ใช่ใหม่ที่สุด — เหตุผลเดียวกับการ์ดยอดรวมบนแดชบอร์ด
    ทั้งพอร์ตสดได้เท่ากับข้อมูลชิ้นที่เก่าที่สุดในนั้นเท่านั้น การโชว์ตัวที่สดที่สุด
    จะทำให้ดูเหมือนข้อมูลใหม่กว่าความจริง
  */
  const oldestQuoteAt = useMemo(() => {
    let oldest: Date | null = null;
    for (const quote of quotesQuery.data?.values() ?? []) {
      if (oldest === null || quote.fetchedAt < oldest) {
        oldest = quote.fetchedAt;
      }
    }
    return oldest;
  }, [quotesQuery.data]);

  const snapshots = snapshotsQuery.data ?? [];
  const latestSnapshot = snapshots.at(-1);

  return (
    <div className="flex max-w-2xl flex-col gap-5">
      <Card className="p-5">
        <SectionLabel>ACCOUNT</SectionLabel>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="size-10 shrink-0 rounded-[6px] bg-linear-to-br from-brand to-down" />
            <div className="min-w-0">
              <div className="truncate text-sm text-ink-100">
                {user?.email ?? user?.displayName ?? EM_DASH}
              </div>
              <div className="font-mono text-[11px] text-ink-500">
                เข้าได้เฉพาะบัญชีที่อยู่ในรายชื่อที่อนุญาต
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-md border border-line-strong px-4 py-2 text-sm text-ink-300 transition hover:border-down hover:text-down"
          >
            ออกจากระบบ
          </button>
        </div>
      </Card>

      <Card className="p-5">
        <SectionLabel>DATA</SectionLabel>
        <p className="mt-2 text-xs leading-relaxed text-ink-500">
          ไว้ดูตอนสงสัยว่าตัวเลขบนหน้าอื่นเก่าหรือยัง
        </p>

        <dl className="mt-4">
          <Row label="สินทรัพย์ที่ถือ">
            {holdingsQuery.isPending ? EM_DASH : `${holdings.length} รายการ`}
          </Row>
          <Row label="ราคาอัปเดตล่าสุด">
            {/*
              พอร์ตว่างต้องบอกว่าไม่มีอะไรให้ดึง ไม่ใช่ปล่อยขีดค้างไว้
              เพราะ query ถูกปิดอยู่ สถานะมันจึงเป็น pending ตลอด หน้าตาเหมือนกำลังโหลด
              ทั้งที่มันจบแล้ว — และ "ยังไม่เคยดึงราคา" ก็คนละเรื่องกับ "ไม่มีอะไรให้ดึง"
            */}
            {holdings.length === 0
              ? 'ยังไม่มีสินทรัพย์'
              : quotesQuery.isPending
                ? EM_DASH
                : oldestQuoteAt === null
                  ? 'ยังไม่เคยดึงราคา'
                  : formatBangkok(oldestQuoteAt)}
          </Row>
          <Row label="ประวัติย้อนหลัง">
            {snapshotsQuery.isPending
              ? EM_DASH
              : latestSnapshot === undefined
                ? 'ยังไม่มี'
                : `${snapshots.length} วัน · ล่าสุด ${latestSnapshot.date}`}
          </Row>
        </dl>
      </Card>

      <Card className="p-5">
        <SectionLabel>APP</SectionLabel>

        <dl className="mt-4">
          <Row label="เวอร์ชัน">{__APP_VERSION__}</Row>
          <Row label="build">
            {formatBangkok(new Date(__BUILD_TIME__))}
            {/* commit ระบุโค้ดได้แน่ชัดกว่าเวลา build ซึ่งต่างกันได้ทั้งที่โค้ดชุดเดียวกัน */}
            <span className="ml-2 text-ink-500">{__COMMIT__}</span>
          </Row>
        </dl>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-sm text-xs leading-relaxed text-ink-500">
            ถ้าเพิ่งมีของใหม่ขึ้นแต่หน้ายังเป็นแบบเดิม
            กดปุ่มนี้เพื่อล้างของที่เครื่องเก็บไว้แล้วโหลดใหม่
          </p>
          <button
            type="button"
            disabled={isReloading}
            onClick={() => {
              setIsReloading(true);
              void reloadLatestBuild();
            }}
            className="rounded-md border border-line-strong px-4 py-2 text-sm text-ink-300 transition hover:border-brand hover:text-brand-text disabled:opacity-60"
          >
            {isReloading ? 'กำลังโหลดใหม่…' : 'โหลดเวอร์ชันล่าสุด'}
          </button>
        </div>
      </Card>
    </div>
  );
}
